import React, { useEffect, useState } from 'react';

import { Alert, Button, LinearProgress, Snackbar, Stack } from '@mui/material';
import axios from 'axios';
import { isMobile, isTablet } from 'react-device-detect';
import { useSearchParams } from 'react-router-dom';

import useRequestFromParams from '../hooks/useRequestFromParams.jsx';
import CaptionAIForm from './form/captionAI-form.jsx';
import CaptionHistory from './history/caption-history.jsx';
import TokensExceededMessage from './popups/tokens-exceeded-message.jsx';
import CaptionAISignIn from './sign-in/captionAI-sign-in.jsx';
import SignUpForm from './sign-in/sign-up-form.jsx';

import '../styles/captionAI.scss';

const CaptionAIApp = () => {
  const mobileView = isMobile || isTablet;

  const requestFromParams = useRequestFromParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showSignIn, setShowSignIn] = useState(false);
  const [clearForm, setClearForm] = useState(false);
  const [loadSession, setLoadSession] = useState(null);
  const [sessionFromParams, setSessionFromParams] = useState(null);
  const [showSignUpForm, setShowSignUpForm] = useState(false);
  const [viewHistory, setViewHistory] = useState(!mobileView);
  const [showTokenCountExceeded, setShowTokenCountExceeded] = useState(false);
  const [contact, setContact] = useState(null);
  const [tokenCount, setTokenCount] = useState(null);
  const [sessionHistory, setSessionHistory] = useState(null);
  const [copyAlertOpen, setCopyAlertOpen] = useState(false);
  const [pollForJWT, setPollForJWT] = useState(null);

  const emailFromParams = searchParams.get('email')
    ? searchParams.get('email')
    : null;
  const [email, setEmail] = useState(emailFromParams);
  const [JWTApplied, setJWTApplied] = useState(null);

  const handleCopyAlertClose = () => {
    setCopyAlertOpen(false);
  };

  const handleCopyAlertOpen = () => {
    setCopyAlertOpen(true);
  };

  const toggleSignIn = () => {
    let view = showSignIn ? 'app' : 'sign-in';
    window.history.pushState(
      { view },
      `CaptionAI ${view}`,
      `/captionai?view=${view}`,
    );

    setShowSignIn(!showSignIn);
  };

  const toggleHistory = () => {
    setViewHistory(!viewHistory);
  };

  // if email is in URL or has been set by sign in, check for JWT
  useEffect(() => {
    if (email || pollForJWT > 0) {
      const token = searchParams.get('token');

      axios
        // if token is not in URL, api will check request for HTTP-only cookie from previous auth
        .get(
          `/api/v1/auth/token?key=${email}&secret=${token}&origin=EXTERNAL`,
          {},
        )
        .then(() => {
          setJWTApplied(true);
        })
        .catch(error => {
          console.log(error);
          setJWTApplied(false);
        });
    } else {
      setJWTApplied(false);
    }
  }, [email, pollForJWT]);

  // Verify email for contact info once JWT has been applied
  useEffect(() => {
    if (JWTApplied) {
      axios
        .post(`/user/verify?email=${email}`, {})
        .then(response => {
          setContact(response.data);
        })
        .catch(error => {
          console.log(error);
        });
    }
  }, [JWTApplied]);

  // Use Contact info for signup form and token info if applicable
  useEffect(() => {
    if (contact && !contact?.surveyComplete) {
      setShowSignUpForm(true);
    }

    if (contact?.id) {
      axios
        .get(`/captionai/tokens`)
        .then(response => {
          setTokenCount({
            current: response.data?.currentUsage,
            max: response.data?.maxUsage,
          });
        })
        .catch(error => {
          console.log(error);
        });

      axios
        .get(`/captionai/sessions/all`)
        .then(response => {
          setSessionHistory(response.data);
        })
        .catch(error => {
          console.log(error);
        });
    }
  }, [contact]);

  useEffect(() => {
    if (requestFromParams) {
      setSessionFromParams(requestFromParams);
    }
  }, [requestFromParams]);

  useEffect(() => {
    if (mobileView) {
      setViewHistory(false);
    }
  }, [loadSession]);

  // Monitor Token Count and show pop-up if amount exceeds allowance
  useEffect(() => {
    if (tokenCount && tokenCount?.current >= tokenCount?.max) {
      setShowTokenCountExceeded(true);
    }
  }, [tokenCount]);

  return (
    <>
      {JWTApplied === null ? (
        <Stack margin={10}>
          <LinearProgress />
        </Stack>
      ) : (
        <Stack direction="column">
          <TokensExceededMessage
            open={showTokenCountExceeded}
            setShowTokenCountExceeded={setShowTokenCountExceeded}
          />
          {showSignUpForm ? (
            <SignUpForm
              open={showSignUpForm}
              setShowSignUpForm={setShowSignUpForm}
              contact={contact}
            />
          ) : (
            <>
              {showSignIn ? (
                <CaptionAISignIn
                  toggleSignIn={toggleSignIn}
                  setEmail={setEmail}
                  setPollForJWT={setPollForJWT}
                  showSignIn={showSignIn}
                />
              ) : (
                <>
                  {contact ? (
                    <div className="captionAI-header-container">
                      {mobileView && contact ? (
                        <Stack direction="row" alignItems="center">
                          <i
                            onClick={toggleHistory}
                            className="fa fa-bars font-awesome-icon-style-light"
                          ></i>
                          <img
                            alt="CaptionAI"
                            src="https://d23ypky16g08sh.cloudfront.net/res/CaptionAIPoweredByLogo.svg"
                          />
                        </Stack>
                      ) : (
                        <img
                          alt="CaptionAI"
                          src="https://d23ypky16g08sh.cloudfront.net/res/CaptionAIPoweredByLogo.svg"
                        />
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="captionAI-no-auth-header">
                        <img src="https://d23ypky16g08sh.cloudfront.net/res/cc-cloud-logo-full-fade.png" />
                        <div className="auth-button-container">
                          <Button
                            variant="outlined"
                            color="cc"
                            onClick={toggleSignIn}
                          >
                            Sign Up
                          </Button>
                          <Button
                            variant="contained"
                            color="cc"
                            onClick={toggleSignIn}
                          >
                            Log In
                          </Button>
                        </div>
                      </div>
                      <div className="captionAI-welcome-text-container">
                        <img src="https://d23ypky16g08sh.cloudfront.net/res/captionAIlogo.svg" alt="CaptionAI" />
                        <h5>
                          Free and effortless social media caption generation
                        </h5>
                      </div>
                    </>
                  )}
                  <div
                    className={
                      contact
                        ? 'captionAI-container with-auth'
                        : 'captionAI-container'
                    }
                  >
                    {contact && viewHistory ? (
                      <CaptionHistory
                        setClearForm={setClearForm}
                        setLoadSession={setLoadSession}
                        sessionHistory={sessionHistory}
                        setSessionHistory={setSessionHistory}
                      />
                    ) : (
                      <></>
                    )}
                    <CaptionAIForm
                      toggleSignIn={toggleSignIn}
                      setClearForm={setClearForm}
                      clearForm={clearForm}
                      contact={contact}
                      setLoadSession={setLoadSession}
                      loadSession={loadSession}
                      preview={false}
                      sessionFromParams={sessionFromParams}
                      tokenCount={tokenCount}
                      setTokenCount={setTokenCount}
                      setSessionHistory={setSessionHistory}
                      sessionHistory={sessionHistory}
                      setShowTokenCountExceeded={setShowTokenCountExceeded}
                      handleCopyAlertOpen={handleCopyAlertOpen}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </Stack>
      )}
      <Snackbar
        open={copyAlertOpen}
        autoHideDuration={5000}
        onClose={handleCopyAlertClose}
      >
        <Alert
          onClose={handleCopyAlertClose}
          severity="success"
          sx={{ width: '100%' }}
        >
          Copied to clipboard
        </Alert>
      </Snackbar>
    </>
  );
};

export default CaptionAIApp;
